import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Providers from './providers';
import { ReactLenis, useLenis } from '@studio-freight/react-lenis';
import { ErrorBoundary, Error, Fallback } from './error/error-handling';
import { Suspense, useEffect, memo, useMemo } from 'react';
import './scss/index.scss';
import './App.css';
import AppRouter from './router';
import { ToastContainer } from 'react-toastify';
// Firebase removed — no longer needed
import PopupNotification from './components/PopupNotification';
import ScrollToTopButton from './components/arrow-to-bottom/ScrollToTop';
import WhatsAppButton from './components/WhatsAppButton';
import AccessibilityControls from './components/AccessibilityControls';
import { KeyboardNavigationProvider } from './utils/keyboardNavigation';
import { initWebVitalsOptimization } from './utils/webVitalsOptimization';
import { useTheme } from './context/ThemeContext';
import { trackSeoEvent } from './utils/analytics';
import { useLowPowerMode } from './utils/performanceMode';

// Initialize Web Vitals optimization (the function has internal checks for production mode)
initWebVitalsOptimization();

// Global error handler
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error);
  // You could send to an error monitoring service here
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // You could send to an error monitoring service here
});

// Memoize components for better performance
const MemoizedToastContainer = memo(() => {
  const { theme } = useTheme();

  return (
    <ToastContainer
      theme={theme}
      position="bottom-right"
      autoClose={5000}
      limit={3}
      pauseOnFocusLoss={false}
    />
  );
});

MemoizedToastContainer.displayName = 'MemoizedToastContainer';

function App() {
  const lowPowerMode = useLowPowerMode();
  const lenisOptions = useMemo(() => ({
    // Native scrolling avoids a second RAF loop on battery-constrained devices.
    lerp: lowPowerMode ? 1 : 0.1,
    syncToNative: lowPowerMode,
    wheelMultiplier: 1,
    smoothWheel: !lowPowerMode,
    infinite: false,
  }), [lowPowerMode]);

  return (
    <Providers>
      {/* syncToNative: framer-motion useScroll reads window.scrollY which Lenis
          keeps in sync — prevents the position-mismatch jitter between Lenis
          and framer-motion's scroll listener on the hero parallax. */}
      <ReactLenis
        root
        autoRaf={!lowPowerMode}
        options={lenisOptions}
      >
        <ErrorBoundary
          error={<Error />}
          onError={(error, errorInfo) => {
            console.error('App Error:', error, errorInfo);
          }}
        >
          <Suspense fallback={<Fallback />}>
            <KeyboardNavigationProvider>
              <RouterScrollToTop />
              <PopupNotification />
              <AppRouter />
              <ScrollToTopButton />
              <WhatsAppButton />
              <FloatingControlsCollisionGuard disabled={lowPowerMode} />
              <AccessibilityControls />
              <MemoizedToastContainer />
              <div className="floating-controls-clearance" aria-hidden="true" />
            </KeyboardNavigationProvider>
          </Suspense>
        </ErrorBoundary>
      </ReactLenis>
    </Providers>
  );
}

// Renamed to RouterScrollToTop to avoid naming conflict with ScrollToTopButton component
const RouterScrollToTop = memo(() => {
  // Extracts pathname property(key) from an object
  const { pathname } = useLocation();
  const lenis = useLenis();

  // Automatically scrolls to top whenever pathname changes
  useEffect(() => {
    trackSeoEvent('page_view', {
      page_location: window.location.href,
      page_path: pathname,
      page_title: document.title,
    });
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
    }
  }, [pathname, lenis]);

  return null;
});

RouterScrollToTop.displayName = 'RouterScrollToTop';

const FloatingControlsCollisionGuard = memo(({ disabled = false }) => {
  useEffect(() => {
    if (disabled) return undefined;
    let animationFrame;
    let delayedCheck;

    const checkControls = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        document.querySelectorAll('.floating-control').forEach((control) => {
          const rect = control.getBoundingClientRect();
          const centerX = rect.left + (rect.width / 2);
          const centerY = rect.top + (rect.height / 2);
          const obscuresAction = rect.width > 0 && rect.height > 0
            && document.elementsFromPoint(centerX, centerY).some((element) => {
              if (control.contains(element) || element.closest('.floating-control')) return false;
              return Boolean(element.closest('a, button, input, textarea, select, [role="button"]'));
            });

          control.classList.toggle('floating-control--obscures-action', obscuresAction);
        });
      });
    };

    const observer = new MutationObserver(checkControls);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', checkControls, { passive: true });
    window.addEventListener('resize', checkControls);
    delayedCheck = window.setTimeout(checkControls, 2300);
    checkControls();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(delayedCheck);
      observer.disconnect();
      window.removeEventListener('scroll', checkControls);
      window.removeEventListener('resize', checkControls);
    };
  }, [disabled]);

  return null;
});

FloatingControlsCollisionGuard.propTypes = {
  disabled: PropTypes.bool,
};

FloatingControlsCollisionGuard.displayName = 'FloatingControlsCollisionGuard';

export default App;
